import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { errorText } from '../../services/video.service';
import { socialService, type SocialAccount, type SocialConfig, type SocialPlatform, type SocialProvider } from '../../services/social.service';
import { FONT_SERIF } from '../landing/landingData';
import { OutlineButton, PageHead, PlusIcon, useDash, useDismiss } from './dashUi';

/**
 * /dashboard/integration — design "HyVay Dashboard" Integration section.
 * A card per platform; "Manage" opens the per-platform list (?manage=<key>) with
 * Disconnect / Reconnect and "Connect account". OAuth comes back through
 * /video/connect/:provider, which returns here.
 */

type CardKey = 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'linkedin' | 'threads' | 'whatsapp';

interface ConnectOption { provider: SocialProvider; label: string; hint: string }
interface CardDef {
    key: CardKey;
    name: string;
    desc: string;
    /** Account platform listed under this card (none = coming soon). */
    platform: SocialPlatform | null;
    options: ConnectOption[];
}

const CARDS: CardDef[] = [
    { key: 'instagram', name: 'Instagram', desc: 'Reels and feed posts', platform: 'instagram', options: [{ provider: 'meta', label: 'Instagram', hint: 'Business or Creator accounts linked to a Facebook Page' }] },
    { key: 'facebook', name: 'Facebook', desc: 'Page posts and Reels', platform: 'facebook', options: [{ provider: 'meta', label: 'Facebook', hint: 'Facebook Pages you manage' }] },
    { key: 'tiktok', name: 'TikTok', desc: 'Short videos', platform: 'tiktok', options: [{ provider: 'tiktok', label: 'TikTok', hint: 'Your TikTok account' }] },
    { key: 'youtube', name: 'YouTube', desc: 'Shorts and long videos', platform: 'youtube', options: [{ provider: 'youtube', label: 'YouTube', hint: 'Your YouTube channel' }] },
    {
        key: 'linkedin', name: 'LinkedIn', desc: 'Video posts on your profile or company page', platform: 'linkedin',
        options: [
            { provider: 'linkedin', label: 'Profile', hint: 'Post as yourself' },
            { provider: 'linkedin_pages', label: 'Company page', hint: 'Pages where you are an admin' },
        ],
    },
    { key: 'threads', name: 'Threads', desc: 'Video posts on your profile', platform: 'threads', options: [{ provider: 'threads', label: 'Threads', hint: 'Your Threads profile' }] },
    { key: 'whatsapp', name: 'WhatsApp Business', desc: 'Status updates and catalog', platform: null, options: [] },
];

const KIND_LABEL: Record<SocialAccount['kind'], string> = {
    channel: 'Channel',
    profile: 'Profile',
    organization: 'Company page',
    page: 'Facebook Page',
    ig_business: 'Business account',
    threads_profile: 'Profile',
};

const fmtSince = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** The OAuth flow that renews an expired account. */
const providerFor = (a: SocialAccount): SocialProvider => {
    if (a.platform === 'facebook' || a.platform === 'instagram') return 'meta';
    if (a.platform === 'linkedin') return a.kind === 'organization' ? 'linkedin_pages' : 'linkedin';
    return a.platform;
};

// ---------------------------------------------------------------- brand marks (design)
const TIKTOK_D = 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';

const BRAND_BG: Record<CardKey, string> = {
    instagram: 'radial-gradient(circle at 30% 107%,#FDD56B 0%,#F8853A 30%,#D6297A 60%,#8A3AB9 85%)',
    facebook: '#0866FF',
    tiktok: '#111111',
    youtube: '#FFFFFF',
    linkedin: '#0A66C2',
    threads: '#000000',
    whatsapp: '#25D366',
};

const BrandMark: React.FC<{ k: CardKey; size?: number }> = ({ k, size = 32 }) => {
    let glyph: React.ReactNode;
    switch (k) {
        case 'instagram':
            glyph = (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                    <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.2" cy="6.8" r="1" fill="#fff" stroke="none" />
                </svg>
            );
            break;
        case 'tiktok':
            glyph = (
                <svg width="16" height="16" viewBox="0 0 24 24">
                    <path fill="#25F4EE" transform="translate(-0.8 -0.6)" d={TIKTOK_D} />
                    <path fill="#FE2C55" transform="translate(0.8 0.6)" d={TIKTOK_D} />
                    <path fill="#fff" d={TIKTOK_D} />
                </svg>
            );
            break;
        case 'youtube':
            glyph = (
                <svg width="20" height="15" viewBox="0 0 28 20">
                    <rect x="0" y="0" width="28" height="20" rx="6" fill="#FF0000" />
                    <path d="M11 5.8v8.4l7.2-4.2z" fill="#fff" />
                </svg>
            );
            break;
        case 'facebook':
            glyph = (
                <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#fff" d="M15.6 7.6h-2c-.6 0-1 .5-1 1.1v2h3l-.4 3.1h-2.6V22h-3.2v-8.2H7v-3.1h2.4V8.4c0-2.6 1.5-4.1 3.9-4.1 1.1 0 2 .1 2.3.1v3.2z" />
                </svg>
            );
            break;
        case 'linkedin':
            glyph = (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                    <rect x="3.5" y="9" width="3.6" height="11.5" rx="0.6" />
                    <circle cx="5.3" cy="5.3" r="2.1" />
                    <path d="M10 9h3.4v1.7c.6-1.1 2-2 3.9-2 3 0 3.7 2 3.7 4.6v7.2h-3.6v-6.3c0-1.4-.3-2.6-1.8-2.6-1.6 0-2 1.1-2 2.6v6.3H10z" />
                </svg>
            );
            break;
        case 'threads':
            glyph = (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                    <path d="M17.2 8.6C16.4 5.6 14.4 4 11.8 4 7.6 4 5 7.2 5 12s2.6 8 6.8 8c3.4 0 6-2 6-4.8 0-2.4-2-3.9-5.2-3.9-2.2 0-3.6 1-3.6 2.4 0 1.3 1.1 2.2 2.6 2.2 2.4 0 3.6-1.9 3.6-5 0-.6 0-1.1-.1-1.6" />
                </svg>
            );
            break;
        case 'whatsapp':
            glyph = (
                <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M12 3.5a8.5 8.5 0 0 0-7.3 12.8L3.5 20.5l4.3-1.1A8.5 8.5 0 1 0 12 3.5z" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
                    <path fill="#fff" d="M9.2 8.3c.2-.4.5-.4.7-.4h.5c.2 0 .4 0 .5.4l.7 1.6c.1.2 0 .4-.1.6l-.5.6c-.1.1-.2.3 0 .5.7 1.2 1.6 2 2.8 2.6.2.1.4.1.5-.1l.6-.7c.2-.2.4-.2.6-.1l1.5.7c.2.1.4.2.4.4 0 .5-.2 1.3-.9 1.7-.6.4-1.6.5-3.2-.2-1.8-.8-3.3-2.4-4-3.7-.7-1.2-.6-2.4-.1-2.9z" />
                </svg>
            );
            break;
    }
    return (
        <span aria-hidden="true" style={{ flex: 'none', width: size, height: size, borderRadius: 8, background: BRAND_BG[k], border: k === 'youtube' ? '1px solid var(--hv-line)' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {glyph}
        </span>
    );
};

const Avatar: React.FC<{ a: SocialAccount; k: CardKey }> = ({ a, k }) => (
    <span style={{ flex: 'none', width: 32, height: 32, borderRadius: 999, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: k === 'youtube' ? '#FF0000' : BRAND_BG[k], color: '#fff', fontSize: 13, fontWeight: 700 }}>
        {a.avatarUrl ? <img src={a.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : a.name.replace(/^@/, '').slice(0, 1).toUpperCase()}
    </span>
);

const Chip: React.FC<{ tone: 'mint' | 'warn' | 'muted'; children: React.ReactNode }> = ({ tone, children }) => {
    const c = tone === 'mint' ? { bg: 'var(--hv-mint)', fg: 'var(--hv-mint-ink)' } : tone === 'warn' ? { bg: 'var(--hv-danger-bg)', fg: 'var(--hv-danger)' } : { bg: 'var(--hv-page-2)', fg: 'var(--hv-body)' };
    return <span style={{ flex: 'none', display: 'flex', alignItems: 'center', height: 16, padding: '0 5px', borderRadius: 999, background: c.bg, color: c.fg, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</span>;
};

const smallBtn: React.CSSProperties = { height: 32, padding: '0 14px', fontSize: 12.5 };

/**
 * Connect button: one provider goes straight to OAuth, several (LinkedIn profile /
 * company page) open a small menu first.
 */
const ConnectButton: React.FC<{ options: ConnectOption[]; onPick: (p: SocialProvider) => void; label: React.ReactNode; busy?: boolean; style?: React.CSSProperties; align?: 'left' | 'right' }> = ({ options, onPick, label, busy, style, align = 'right' }) => {
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss<HTMLDivElement>(open, close);
    if (options.length === 1) return <OutlineButton disabled={busy} onClick={() => onPick(options[0].provider)} style={style}>{label}</OutlineButton>;
    return (
        <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
            <OutlineButton disabled={busy} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={style}>{label}</OutlineButton>
            {open && (
                <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', [align]: 0, zIndex: 30, width: 240, padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 24px 44px -20px var(--hv-shadow)', animation: 'hvPop 0.18s ease both' }}>
                    {options.map((o) => (
                        <button key={o.provider} type="button" role="menuitem" className="hv-mi" onClick={() => { setOpen(false); onPick(o.provider); }} style={{ width: '100%', display: 'block', padding: '9px 10px', border: 0, borderRadius: 5, background: 'transparent', color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left' }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{o.label}</div>
                            <div style={{ marginTop: 2, fontSize: 12, color: 'var(--hv-muted)' }}>{o.hint}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const YOUTUBE_CONSENT = (
    <>
        HyVay uses YouTube API Services. By connecting YouTube you agree to the{' '}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>YouTube Terms of Service</a>, the{' '}
        <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>HyVay Privacy Policy</a> and the{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>Google Privacy Policy</a>. You can revoke access at any time with Disconnect here or in your{' '}
        <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noreferrer" style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>Google security settings</a>.
    </>
);

// ---------------------------------------------------------------- page
export const DashIntegration: React.FC = () => {
    const dash = useDash();
    const confirm = useConfirm();
    const [params, setParams] = useSearchParams();
    const [cfg, setCfg] = useState<SocialConfig | null | undefined>(undefined);
    const [accounts, setAccounts] = useState<SocialAccount[] | null>(null);
    const [connecting, setConnecting] = useState(false);

    const load = useCallback(() => socialService.accounts().then(setAccounts).catch(() => setAccounts([])), []);
    useEffect(() => {
        socialService.config().then(setCfg).catch(() => setCfg(null));
        void load();
    }, [load]);

    const manageKey = params.get('manage') as CardKey | null;
    const managing = CARDS.find((c) => c.key === manageKey && c.platform) || null;
    const openManage = (k: CardKey | null) => {
        setParams(k ? { manage: k } : {});
        document.querySelector('.hv-db-main')?.scrollTo?.({ top: 0 });
    };

    const byCard = useMemo(() => {
        const m = {} as Record<CardKey, SocialAccount[]>;
        for (const c of CARDS) m[c.key] = c.platform ? (accounts || []).filter((a) => a.platform === c.platform) : [];
        return m;
    }, [accounts]);

    const enabledOptions = (c: CardDef) => (cfg?.enabled ? c.options.filter((o) => cfg.providers[o.provider]) : []);

    const connect = async (p: SocialProvider) => {
        if (p === 'youtube') {
            const ok = await confirm({ title: 'Connect YouTube', eyebrow: 'BEFORE YOU CONTINUE', tone: 'default', description: YOUTUBE_CONSENT, confirmLabel: 'Continue to Google' });
            if (!ok) return;
        }
        setConnecting(true);
        try {
            await socialService.connect(p); // leaves the page on success
        } catch (e) {
            setConnecting(false);
            dash.notify('error', errorText(e, 'Could not start the connection. Please try again.'));
        }
    };

    const disconnect = async (a: SocialAccount) => {
        // Count what is still queued for this account, so the dialog can say what gets canceled.
        const pending = await socialService
            .posts()
            .then((ps) => ps.filter((p) => p.account.id === a.id && (p.status === 'SCHEDULED' || p.status === 'PUBLISHING')).length)
            .catch(() => null);
        let canceled = 0;
        let purge = false;
        const ok = await confirm({
            title: `Disconnect ${a.name}?`,
            // Plain disconnect is reversible (reconnect revives the history); only the purge option is not.
            eyebrow: 'DISCONNECT ACCOUNT',
            description: (
                <>
                    {pending ? <><b>{pending} scheduled post{pending === 1 ? '' : 's'}</b> to this account will be canceled. </> : pending === null ? 'Any scheduled posts to this account will be canceled. ' : ''}
                    HyVay stops posting to this account. Posts already published stay on the platform, and your HyVay post history stays in Analytics. Connect the same account again later to pick up where you left off.
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
                        <input type="checkbox" onChange={(e) => { purge = e.target.checked; }} style={{ marginTop: 3, accentColor: 'var(--hv-danger)' }} />
                        <span>Also delete this account's post history and statistics from HyVay. This cannot be undone.</span>
                    </label>
                </>
            ),
            confirmLabel: 'Disconnect',
            busyLabel: 'Disconnecting…',
            onConfirm: async () => {
                try {
                    canceled = (await socialService.disconnect(a.id, purge)).canceled;
                } catch (e) {
                    throw new Error(errorText(e, 'Could not disconnect. Please try again.'));
                }
            },
        });
        if (!ok) return;
        dash.notify('success', canceled ? `${a.name} disconnected · ${canceled} scheduled post${canceled === 1 ? '' : 's'} canceled` : `${a.name} disconnected`);
        void load();
    };

    const notReady = cfg === null || (cfg && !cfg.enabled);

    if (managing) {
        const rows = byCard[managing.key];
        const opts = enabledOptions(managing);
        const addBtn = (extra?: React.CSSProperties) =>
            opts.length ? (
                <ConnectButton options={opts} onPick={connect} busy={connecting} style={extra} label={<><PlusIcon />Connect account</>} />
            ) : (
                <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>Not available yet</span>
            );
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <nav aria-label="Breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--hv-muted)' }}>
                        <button type="button" className="hv-link" onClick={() => openManage(null)} style={{ padding: 0, border: 0, background: 'transparent', font: 'inherit', color: 'var(--hv-body)', cursor: 'pointer', textUnderlineOffset: 3, textDecorationThickness: 1 }}>Integration</button>
                        <span aria-hidden="true">/</span>
                        <span style={{ color: 'var(--hv-ink)' }}>{managing.name}</span>
                    </nav>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <BrandMark k={managing.key} size={36} />
                        <div style={{ flex: 1, minWidth: 200 }}>
                            <h1 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1, letterSpacing: '-0.008em', wordSpacing: '0.06em' }}>{managing.name}</h1>
                            <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--hv-body)' }}>{managing.desc}. Connect as many accounts as you need.</p>
                        </div>
                        {addBtn()}
                    </div>
                    {(managing.key === 'instagram' || managing.key === 'facebook') && opts.length > 0 && (
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--hv-muted)', lineHeight: 1.55 }}>
                            Facebook and Instagram connect together through Meta: Instagram Business or Creator accounts must be linked to a Facebook Page.
                        </p>
                    )}
                    {managing.key === 'youtube' && opts.length > 0 && <p style={{ margin: 0, fontSize: 12, color: 'var(--hv-muted)', lineHeight: 1.55 }}>{YOUTUBE_CONSENT}</p>}
                </div>

                {accounts === null ? (
                    <div style={{ padding: '48px 20px', textAlign: 'center', fontSize: 13, color: 'var(--hv-muted)' }}>Loading accounts…</div>
                ) : rows.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className="hv-db-row" style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '0 20px', height: 40, background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-body)', gridTemplateColumns: 'minmax(220px,2.4fr) minmax(120px,1fr) minmax(90px,auto)' }}>
                            <span>Account</span>
                            <span className="hv-db-hide-sm">Connected on</span>
                            <span />
                        </div>
                        {rows.map((a) => {
                            const expired = a.status === 'EXPIRED';
                            return (
                                <div key={a.id} className="hv-db-row" style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '10px 20px', minHeight: 60, borderBottom: '1px solid var(--hv-line)', gridTemplateColumns: 'minmax(220px,2.4fr) minmax(120px,1fr) minmax(90px,auto)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                        <Avatar a={a} k={managing.key} />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                                <span style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                                                {expired && <Chip tone="warn">Reconnect needed</Chip>}
                                            </div>
                                            <div style={{ marginTop: 2, fontSize: 12, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{KIND_LABEL[a.kind] || managing.name}</div>
                                        </div>
                                    </div>
                                    <span className="hv-db-hide-sm" style={{ fontSize: 13, color: 'var(--hv-ink)' }}>{fmtSince(a.createdAt)}</span>
                                    <div style={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 14 }}>
                                        {expired && cfg?.enabled && cfg.providers[providerFor(a)] && (
                                            <button type="button" className="hv-link" disabled={connecting} onClick={() => void connect(providerFor(a))} style={{ padding: 0, border: 0, background: 'transparent', color: 'var(--hv-brand-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textUnderlineOffset: 3, textDecorationThickness: 1 }}>Reconnect</button>
                                        )}
                                        <button type="button" className="hv-link" data-track="social_disconnect" data-track-label={a.platform} onClick={() => void disconnect(a)} style={{ padding: 0, border: 0, background: 'transparent', color: 'var(--hv-danger)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textUnderlineOffset: 3, textDecorationThickness: 1 }}>Disconnect</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '48px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600 }}>No {managing.name} accounts yet</div>
                        <p style={{ margin: 0, maxWidth: 340, fontSize: 13, lineHeight: 1.55, color: 'var(--hv-body)' }}>Connect an account to schedule and post videos to it.</p>
                        <div style={{ marginTop: 6 }}>{addBtn()}</div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <PageHead title="Integration" lead="Connect your accounts to post videos and share your sites." />
            {notReady && (
                <div style={{ padding: '12px 16px', borderRadius: 8, border: '1px solid var(--hv-line)', background: 'var(--hv-card-2)', fontSize: 13, color: 'var(--hv-body)' }}>
                    Posting to social accounts isn't available yet. You can still manage accounts you connected before.
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
                {CARDS.map((c) => {
                    const list = byCard[c.key];
                    const n = list.length;
                    const expired = list.filter((a) => a.status === 'EXPIRED').length;
                    const soon = !c.platform;
                    const opts = enabledOptions(c);
                    const loading = accounts === null || cfg === undefined;
                    const countLabel = soon ? 'Coming soon' : loading ? 'Loading…' : expired ? `Reconnect needed · ${expired} of ${n} account${n === 1 ? '' : 's'}` : n ? `${n} account${n === 1 ? '' : 's'} connected` : 'Not connected';
                    const countFg = expired ? 'var(--hv-danger)' : n ? 'var(--hv-ink)' : 'var(--hv-muted)';
                    return (
                        <div key={c.key} style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 18, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <BrandMark k={c.key} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                        <span style={{ fontSize: 14.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                                        {n > 0 && <Chip tone="mint">Connected</Chip>}
                                        {soon && <Chip tone="muted">Coming soon</Chip>}
                                    </div>
                                    <div style={{ marginTop: 2, fontSize: 12.5, color: 'var(--hv-body)' }}>{c.desc}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 32 }}>
                                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: countFg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{countLabel}</span>
                                {!soon && !loading && (n > 0 ? (
                                    <OutlineButton onClick={() => openManage(c.key)} style={{ ...smallBtn, fontWeight: 500 }}>{expired ? 'Reconnect' : 'Manage'}</OutlineButton>
                                ) : opts.length ? (
                                    <ConnectButton options={opts} onPick={connect} busy={connecting} label="Connect" style={{ ...smallBtn, fontWeight: 700, background: 'transparent' }} />
                                ) : (
                                    <span style={{ flex: 'none', fontSize: 12.5, color: 'var(--hv-muted)' }}>Not available yet</span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: 'var(--hv-muted)', maxWidth: 620 }}>
                HyVay asks for permission to publish on your behalf and to read the statistics of the posts it publishes. Access tokens are stored encrypted. You can disconnect an account at any time: that deletes its tokens, and you can choose to delete its HyVay post history too.
            </p>
        </div>
    );
};

export default DashIntegration;
